import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile
} from 'firebase/auth';
import { auth, db } from './firebase';
import { getUserData, updateUserData } from './user-service';
import { grantPassCodeurAdmin } from './stripe-service';
import { doc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';

// Création du contexte
const AuthContext = createContext();

// Hook personnalisé pour utiliser le contexte d'authentification
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider du contexte
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fonction pour vérifier si un alias est disponible
  const isAliasAvailable = async (alias) => {
    console.log('[FIREBASE] Requête: isAliasAvailable', { alias });
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('alias', '==', alias));
      const querySnapshot = await getDocs(q);
      return querySnapshot.empty;
    } catch (error) {
      console.error("Erreur lors de la vérification de l'alias:", error);
      throw error;
    }
  };

  // Fonction pour mettre à jour l'alias
  const updateAlias = async (alias) => {
    if (!currentUser) throw new Error("Aucun utilisateur connecté");
    console.log('[FIREBASE] Requête: updateAlias', { userId: currentUser.uid, alias });
    try {
      // Vérifier si l'alias est disponible
      const isAvailable = await isAliasAvailable(alias);
      if (!isAvailable) {
        throw new Error("Cet alias est déjà utilisé");
      }

      // Mettre à jour l'alias dans Firestore
      await updateUserData(currentUser.uid, { alias });
      setUserData(prev => ({ ...prev, alias }));
      return { success: true };
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'alias:", error);
      throw error;
    }
  };

  // Fonction pour s'inscrire avec email et mot de passe
  const register = async (email, password, displayName) => {
    console.log('[FIREBASE] Requête: register', { email, displayName });
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      // Mettre à jour le profil avec le nom d'affichage
      await updateProfile(userCredential.user, { displayName });
      
      // Créer un document utilisateur dans Firestore
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        userType: 'normal',
        createdAt: new Date().toISOString(),
        alias: null // Alias initialisé à null
      });

      // Donner automatiquement le Pass Codeur à tout nouvel inscrit
      try {
        await grantPassCodeurAdmin(userCredential.user.uid);
      } catch (grantError) {
        console.error("Erreur lors de l'attribution automatique du Pass Codeur:", grantError);
        // On ne bloque pas l'inscription si l'attribution échoue
      }
      
      return userCredential.user;
    } catch (error) {
      console.error("Erreur lors de l'inscription:", error);
      throw error;
    }
  };

  // Fonction pour se connecter avec email et mot de passe
  const login = (email, password) => {
    console.log('[FIREBASE] Requête: login', { email });
    return signInWithEmailAndPassword(auth, email, password);
  };

  // Fonction pour se connecter avec Google
  const loginWithGoogle = async () => {
    console.log('[FIREBASE] Requête: loginWithGoogle');
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Vérifier si c'est la première connexion pour créer le profil utilisateur
      try {
        const isFirstLogin = result.user?.metadata?.creationTime === result.user?.metadata?.lastSignInTime;
        if (isFirstLogin) {
          await setDoc(doc(db, 'users', result.user.uid), {
            userType: 'normal',
            createdAt: new Date().toISOString(),
            alias: null // Alias initialisé à null
          }, { merge: true });

          // Donner automatiquement le Pass Codeur aux nouveaux comptes Google
          try {
            await grantPassCodeurAdmin(result.user.uid);
          } catch (grantError) {
            console.error("Erreur lors de l'attribution automatique du Pass Codeur (Google):", grantError);
          }
        }
      } catch (firestoreError) {
        console.error("Erreur lors de la création du profil utilisateur:", firestoreError);
        // Continuer malgré l'erreur, l'utilisateur est authentifié
      }
      
      return result;
    } catch (error) {
      console.error("Erreur lors de la connexion avec Google:", error);
      throw error;
    }
  };

  // Fonction pour se déconnecter
  const logout = () => {
    console.log('[FIREBASE] Requête: logout');
    return signOut(auth);
  };
  
  // Fonction pour mettre à jour les préférences utilisateur
  const updateUserPreferences = async (data) => {
    if (!currentUser) throw new Error("Aucun utilisateur connecté");
    try {
      await updateUserData(currentUser.uid, data);
      setUserData(prev => ({ ...prev, ...data }));
      return { success: true };
    } catch (error) {
      console.error("Erreur lors de la mise à jour des préférences:", error);
      throw error;
    }
  };

  // Effet pour surveiller l'état d'authentification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setError(null);
      
      if (user) {
        try {
          // Charger les données utilisateur depuis Firestore
          const data = await getUserData(user.uid);
          setUserData(data);
        } catch (error) {
          console.error("Erreur lors du chargement des données utilisateur:", error);
          setError("Impossible de charger les données utilisateur");
          // Définir des valeurs par défaut pour éviter les erreurs
          setUserData({
            userType: 'normal'
          });
        }
      } else {
        setUserData(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Valeur du contexte
  const value = {
    currentUser,
    userData,
    error,
    login,
    register,
    logout,
    loginWithGoogle,
    updateUserPreferences,
    updateAlias,
    isAliasAvailable
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 